<script setup lang="ts">
  import type { StashColorNamesWithShades } from '@leaflink/stash-types/colors';
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, Fragment, inject, ref, useAttrs, useCssModule, type VNode, watch } from 'vue';

  import { t } from '../../locale';
  import type { CheckboxProps } from '../Checkbox/Checkbox.vue';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import ChevronToggle from '../ChevronToggle/ChevronToggle.vue';
  import Expand from '../Expand/Expand.vue';
  import { TABLE_INJECTION } from '../Table/Table.vue';
  import TableCell from '../TableCell/TableCell.vue';

  defineOptions({ inheritAttrs: false });

  export interface TableRowProps {
    hideExpansionDivider?: boolean;
    isExpanded?: boolean;
    isSelected?: boolean;
    isSelectDisabled?: boolean;
    /**
     * The accent color for the TableRow.
     */
    accentColor?: StashColorNamesWithShades;
  }

  const props = withDefaults(defineProps<TableRowProps>(), {
    /**
     * If true, hides the divider between the parent row and expansion row
     */
    hideExpansionDivider: false,
    isExpanded: false,
    isSelected: false,
    isSelectDisabled: false,
    accentColor: undefined,
  });

  const emit = defineEmits<{
    'update:isSelected': [isChecked: CheckboxProps['checked']];
    'update:isExpanded': [isExpanded: boolean];
  }>();

  const attrs = useAttrs();
  const classes = useCssModule();
  const slots = defineSlots<{
    default(props: { isRowExpanded?: boolean; toggleExpand: (value?: boolean) => void }): VNode[];
    expansion(): unknown[];
  }>();

  const tableInjection = inject(TABLE_INJECTION.key);

  if (!tableInjection) {
    throw new Error('TableRow must be used within a Table component');
  }

  const { hasActions, hasCustomExpandToggle, isSchemaDriven, isExpandable, isSelectable, layout, isLoading } =
    tableInjection;

  const showSelectionCell = computed(() => isSelectable.value && !isSchemaDriven?.value);

  const isRowExpanded = ref(props.isExpanded);
  const isRowExpandable = computed(() => isExpandable.value && !!slots.expansion);

  function isVNodeArray(children: unknown): children is VNode[] {
    return (
      Array.isArray(children) && children.every((c): c is VNode => c != null && typeof c === 'object' && 'type' in c)
    );
  }

  // Children passed as part of a for loop have a different shape than children passed normally.
  function countSlotChildren(vnodes: VNode[]): number {
    return vnodes.reduce((total, node) => {
      if (node.type === Fragment && isVNodeArray(node.children)) {
        return total + countSlotChildren(node.children);
      }
      return total + 1;
    }, 0);
  }

  const childCellCount = computed(() => {
    const vnodes = slots.default?.({ toggleExpand }) ?? [];
    let count = countSlotChildren(vnodes);

    if (showSelectionCell.value) {
      count += 1;
    }

    if (isRowExpandable.value && !hasCustomExpandToggle.value) {
      count += 1;
    }

    return count;
  });
  const rowId = uniqueId('table-row-');

  function toggleExpand(value?: boolean) {
    if (!isRowExpandable.value) {
      throw new Error('Cannot call `toggleExpand` on a non-expandable row. Provide an `expansion` slot.');
    }

    const isExpanded = typeof value === 'boolean' ? value : !isRowExpanded.value;

    isRowExpanded.value = isExpanded;
    emit('update:isExpanded', isExpanded);
  }

  watch(
    () => props.isExpanded,
    () => toggleExpand(props.isExpanded),
  );
</script>

<template>
  <tr
    class="stash-table-row"
    :class="[
      classes.root,
      classes[`layout--${layout}`],
      {
        [classes['is-expandable']]: isRowExpandable,
        [classes['is-expanded']]: isRowExpanded,
        [classes['root--hidden-divider']]: hideExpansionDivider,
        'p-gutter': layout === 'stack',
        'mb-6 shadow lg:mb-0 lg:shadow-none': layout === 'stack' && !isRowExpandable,
        'pt-[60px]': hasActions && layout === 'stack' && !showSelectionCell && !isLoading,
        'stash-table-row--accent': props.accentColor,
        relative: props.accentColor,
      },
    ]"
    data-test="stash-table-row"
    v-bind="attrs"
  >
    <TableCell
      v-if="showSelectionCell"
      is-control
      class="stash-table-row__selection-cell min-w-12 lg:pl-3"
      :class="classes['row-control-cell']"
      data-test="stash-table-row|selection-cell"
    >
      <Checkbox
        :class="classes['row-selection-checkbox']"
        :checked="props.isSelected"
        :disabled="props.isSelectDisabled"
        :title="t('ll.select.self')"
        @update:checked="emit('update:isSelected', $event)"
      />
    </TableCell>
    <TableCell
      v-if="isRowExpandable && !hasCustomExpandToggle"
      class="stash-table-row__toggle-expansion-cell px-0"
      data-test="stash-table-row|custom-expansion-cell"
      is-control
      :class="classes['row-control-cell']"
    >
      <ChevronToggle
        :aria-controls="rowId"
        :aria-label="isRowExpanded ? t('ll.table.collapseRow') : t('ll.table.expandRow')"
        :direction="isRowExpanded ? 'up' : 'down'"
        :is-expanded="isRowExpanded"
        @click="toggleExpand"
      />
    </TableCell>
    <!-- @slot default -->
    <slot :is-row-expanded="isRowExpanded" :toggle-expand="toggleExpand"></slot>
    <!-- Displays a 4px accent on the left of the table row -->
    <div
      v-if="props.accentColor"
      data-test="stash-table-row|accent"
      :class="{
        absolute: props.accentColor,
        [`bg-${props.accentColor}`]: props.accentColor,
        block: props.accentColor,
        'h-full': props.accentColor,
        'w-1': props.accentColor,
        'top-0': props.accentColor,
        'left-0': props.accentColor,
      }"
    ></div>
  </tr>
  <tr
    v-if="isRowExpandable"
    class="stash-table-row stash-table-row--expandable"
    :class="classes['row-expansion']"
    data-test="stash-table-row|expansion-row"
  >
    <Expand
      is="td"
      :id="rowId"
      class="border-none bg-white px-3 py-0"
      data-test="stash-table-row|expansion-cell"
      :class="classes['row-expansion-content']"
      :colspan="childCellCount"
      :is-expanded="isRowExpanded"
    >
      <!-- @slot expansion, for expandable content -->
      <slot name="expansion"></slot>
    </Expand>
  </tr>
</template>

<style module>
  @reference "../../../styles/main.css";

  .root {
    border: 0;
  }

  /**
 * Only takes effect when hideExpansionDivider is true, for a softer collapse transition as the bottom border re-appears
 */
  .is-expandable > td {
    border-bottom-color: var(--color-ice-200);
    transition: border-bottom-color 0.5s;
  }

  .root--hidden-divider.is-expanded > td {
    transition: border-bottom-color 0s;
    /**
   * Removes the grey cell background for 'control' cells at lower breakpoints, letting the white of the tr come through.
   */
    border-bottom-color: transparent;
  }

  .layout--scroll {
    border-radius: 0;
    box-shadow: none !important;
    margin: 0;
  }

  .layout--scroll:last-of-type {
    border: 0;
  }

  .layout--scroll:last-of-type td {
    border-bottom: none;
  }

  .layout--stack {
    align-items: stretch;
    background-color: var(--color-white);
    column-gap: var(--grid-gutter);
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    position: relative;
    row-gap: var(--grid-gutter);
  }

  .layout--stack:not(.is-expanded) {
    border-radius: var(--radius-sm);
    transition: border-radius 0.5s;
  }

  .layout--stack.is-expanded {
    border-top-left-radius: var(--radius-sm);
    border-top-right-radius: var(--radius-sm);
  }

  .row-selection-checkbox label {
    padding: 0;
    vertical-align: middle;
  }

  .layout--stack .row-control-cell {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  /* On mobile, display controls on their own row, pushing other cells below */
  .row-control-cell + :not(.row-control-cell) {
    grid-column-start: 1;
  }

  .row-expansion-content {
    box-shadow: inset 0 -1px 0 0 var(--color-ice-200);
  }

  .layout--stack + .row-expansion,
  .layout--stack + .row-expansion > .row-expansion-content {
    display: block;
  }

  .layout--stack + .row-expansion > .row-expansion-content {
    border-top: 1px solid var(--color-ice-200);
    border-bottom-left-radius: var(--radius-sm);
    border-bottom-right-radius: var(--radius-sm);
    margin-bottom: --spacing(6);
  }

  .layout--stack.root--hidden-divider + .row-expansion > .row-expansion-content {
    border-top: none;
  }

  /* On large screens, match the styles for layout-SCROLL */
  @media (width >= theme(--breakpoint-lg)) {
    .layout--stack {
      align-items: unset;
      background-color: unset;
      border: 0;
      border-bottom: 1px solid var(--color-ice-200);
      border-radius: 0;
      box-shadow: none;
      column-gap: unset;
      display: table-row;
      grid-template-columns: unset;
      row-gap: unset;
    }

    .layout--stack:last-of-type {
      border-bottom: 0;
    }

    .layout--stack .row-control-cell {
      display: table-cell;
    }

    .layout--stack + .row-expansion {
      display: table-row;
    }

    .layout--stack + .row-expansion > .row-expansion-content {
      border-top: unset;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      display: table-cell;
      margin-bottom: unset;
    }

    .row-control-cell + :not(.row-control-cell) {
      grid-column-start: unset;
    }
  }
</style>
